import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, InputAdornment, MenuItem,
  Paper, Stack, Tab, Tabs, TextField, Typography, Skeleton, alpha, useTheme,
} from '@mui/material';
import {
  ArrowBack, Send, Build, SentimentSatisfiedAlt, Sync,
  Search, CheckCircle, Work, FilterList, ClearAll, Payments, Visibility, Block, LockOpen,
} from '@mui/icons-material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import { getCustomerGodView } from '../../services/opsViewService';
import api from '../../services/api';
import { opsEntityPath } from '../../utils/opsEntityNav';
import { formatMoney } from '../../utils/currency';

const TEAL = '#0d9488';

const TABS = [
  { key: 'request', label: 'Request', icon: <Send fontSize="small" /> },
  { key: 'complaint', label: 'Complaint', icon: <Build fontSize="small" /> },
  { key: 'rating', label: 'Rating', icon: <SentimentSatisfiedAlt fontSize="small" /> },
  { key: 'referral', label: 'Referral', icon: <Sync fontSize="small" /> },
] as const;

const STATUS_BY_TAB: Record<string, { value: string; label: string }[]> = {
  request: [
    { value: 'searching', label: 'Searching' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'started', label: 'Started' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'delivered', label: 'Delivered' },
  ],
  complaint: [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'closed', label: 'Closed' },
  ],
};

export default function CustomerGodViewPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { customerId = '' } = useParams<{ customerId: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from || '/users';
  const backLabel = backTo.includes('/hubs/') ? 'Service users' : 'Users';

  const queryClient = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('request');
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockReason, setBlockReason] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const statusOptions = STATUS_BY_TAB[tab] || [];
  const extraFilterCount = [status, from, to].filter(Boolean).length;

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['customer-god-view', customerId, tab, search, status, from, to],
    queryFn: () => getCustomerGodView(customerId, { tab, search, status, from, to, limit: 100 }),
    enabled: !!customerId,
    retry: 1,
  });

  const refreshCustomer = () => queryClient.invalidateQueries({ queryKey: ['customer-god-view'] });
  const blockMut = useMutation({
    mutationFn: (reason: string) => api.put(`/admin/users/${customerId}/block`, { reason }),
    onSuccess: () => { refreshCustomer(); setBlockOpen(false); setBlockReason(''); },
  });
  const unblockMut = useMutation({
    mutationFn: () => api.put(`/admin/users/${customerId}/unblock`, {}),
    onSuccess: refreshCustomer,
  });

  const profile = data?.customer;
  const stats = data?.stats;
  const rows = data?.rows || [];
  const total = data?.total ?? rows.length;
  const name = profile?.name
    || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim()
    || 'Customer';
  const tabLabel = TABS.find((t) => t.key === tab)?.label || 'Request';

  const openRow = (row: Record<string, unknown>) => {
    if (tab === 'complaint' && row._id) {
      navigate(`/helpdesk?ticketId=${row._id}`);
      return;
    }
    const path = opsEntityPath(row);
    if (path) navigate(path, { state: { from: `/customers/${customerId}` } });
  };

  const columns: GridColDef[] = useMemo(() => {
    if (tab === 'complaint') {
      return [
        { field: 'ticketNumber', headerName: 'Ticket', width: 130 },
        { field: 'subject', headerName: 'Subject', flex: 1, minWidth: 160 },
        { field: 'category', headerName: 'Category', width: 110 },
        { field: 'priority', headerName: 'Priority', width: 100 },
        { field: 'status', headerName: 'Status', width: 120 },
        { field: 'createdAt', headerName: 'Date', width: 150, valueGetter: (_v, row) => row.createdAt ? new Date(row.createdAt).toLocaleString() : '-' },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'View',
          width: 80,
          getActions: (p) => [
            <GridActionsCellItem
              key="view"
              icon={<Visibility fontSize="small" />}
              label="View"
              onClick={() => navigate(`/helpdesk?ticketId=${p.row._id}`)}
            />,
          ],
        },
      ];
    }
    if (tab === 'rating') {
      return [
        { field: 'reviewer', headerName: 'Partner', flex: 1, minWidth: 140 },
        { field: 'rating', headerName: 'Rating', width: 90 },
        { field: 'review', headerName: 'Review', flex: 1.4, minWidth: 180 },
        { field: 'entityType', headerName: 'Type', width: 120 },
        { field: 'createdAt', headerName: 'Date', width: 150, valueGetter: (_v, row) => row.createdAt ? new Date(row.createdAt).toLocaleString() : '-' },
      ];
    }
    if (tab === 'referral') {
      return [
        { field: 'firstName', headerName: 'Name', flex: 1, minWidth: 140, valueGetter: (_v, row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || '-' },
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 140 },
        { field: 'phone', headerName: 'Contact', width: 130 },
        { field: 'createdAt', headerName: 'Joined', width: 150, valueGetter: (_v, row) => row.createdAt ? new Date(row.createdAt).toLocaleString() : '-' },
      ];
    }
    return [
      { field: 'requestNumber', headerName: 'Request Number', width: 160 },
      { field: 'pickup', headerName: 'Pickup Location', flex: 1, minWidth: 160 },
      { field: 'drop', headerName: 'Drop Location', flex: 1, minWidth: 160 },
      { field: 'partner', headerName: 'Partner', width: 140 },
      { field: 'totalAmount', headerName: 'Total Amount', width: 130, valueGetter: (_v, row) => `${formatMoney(row.totalAmount ?? 0, row)}` },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (p) => <Chip size="small" label={String(p.value || '').replace(/_/g, ' ')} />,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'View',
        width: 80,
        getActions: (p) => [
          <GridActionsCellItem
            key="view"
            icon={<Visibility fontSize="small" />}
            label="View"
            onClick={() => openRow(p.row as Record<string, unknown>)}
          />,
        ],
      },
    ];
  }, [tab, customerId]);

  const clearFilters = () => {
    setSearch('');
    setStatus('');
    setFrom('');
    setTo('');
  };

  const goBack = () => {
    if (window.history.length > 1) navigate(backTo);
    else navigate('/users');
  };

  return (
    <PageShell maxWidth={false}>
      <PageHeader
        eyebrow="Customers"
        title={name}
        subtitle="Customer god view — trips, orders, complaints, ratings, and referrals"
        breadcrumbs={[
          { label: backLabel, href: backTo },
          { label: name },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            {profile?.isBlocked || profile?.status === 'Blocked' ? (
              <Button
                startIcon={<LockOpen />}
                color="success"
                variant="contained"
                onClick={() => unblockMut.mutate()}
                disabled={unblockMut.isPending}
              >
                Unblock
              </Button>
            ) : (
              <Button
                startIcon={<Block />}
                color="warning"
                variant="contained"
                onClick={() => setBlockOpen(true)}
              >
                Block
              </Button>
            )}
            <Button startIcon={<ArrowBack />} variant="outlined" onClick={goBack}>
              Back
            </Button>
          </Stack>
        }
      />

      {isError && (
        <Paper sx={{ p: 3, mb: 2, borderRadius: 2 }}>
          <Typography color="error" fontWeight={700} gutterBottom>
            Could not load this customer profile.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {(error as { response?: { data?: { message?: string } } })?.response?.data?.message
              || 'The customer may have been removed or you may not have permission to view this profile.'}
          </Typography>
          <Button sx={{ mt: 2 }} variant="outlined" onClick={goBack}>Back to {backLabel}</Button>
        </Paper>
      )}

      {!isError && (
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={3} lg={3}>
          <Paper elevation={0} sx={{ p: 2.5, height: '100%', borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
            {isLoading && !profile ? (
              <Stack alignItems="center" spacing={1}>
                <Skeleton variant="circular" width={84} height={84} />
                <Skeleton width={140} />
                <Skeleton width={180} />
              </Stack>
            ) : (
              <>
                <Stack alignItems="center" spacing={1}>
                  <Avatar sx={{ width: 84, height: 84, bgcolor: 'grey.200', fontSize: 32, color: 'text.secondary' }}>
                    {name.slice(0, 1).toUpperCase()}
                  </Avatar>
                  <Typography fontWeight={800}>{name}</Typography>
                  <Chip
                    size="small"
                    label={profile?.status || 'Active'}
                    color={profile?.isBlocked || profile?.status === 'Blocked' ? 'error' : 'success'}
                  />
                  {(profile?.isBlocked || profile?.status === 'Blocked') && profile?.blockedReason ? (
                    <Typography variant="caption" color="error.main" textAlign="center">
                      {profile.blockedReason}
                    </Typography>
                  ) : null}
                  <Box sx={{ px: 1.25, py: 0.35, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="caption">{profile?.email || '—'}</Typography>
                  </Box>
                </Stack>
                <Grid container spacing={1} sx={{ mt: 2 }}>
                  {[
                    { label: 'Rating', value: stats?.rating ?? 0, icon: <CheckCircle sx={{ color: '#16a34a', fontSize: 28 }} /> },
                    { label: 'Trip', value: stats?.tripCount ?? 0, icon: <Work sx={{ color: TEAL, fontSize: 28 }} /> },
                  ].map((s) => (
                    <Grid item xs={6} key={s.label}>
                      <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center', borderRadius: 2 }}>
                        {s.icon}
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                          {s.value} {s.label}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 1.25, textAlign: 'center', borderRadius: 2 }}>
                      <Payments sx={{ color: '#f59e0b', fontSize: 28 }} />
                      <Typography variant="body2" fontWeight={700} sx={{ mt: 0.5 }}>
                        {formatMoney(stats?.spend ?? 0)} Total spend
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', my: 2 }} />
                <Stack spacing={1.25}>
                  {[
                    ['Username', profile?.username || profile?.firstName],
                    ['Status', profile?.status],
                    ['Contact', profile?.phone],
                    ['Orders', String(stats?.orderCount ?? 0)],
                    ['Wallet', `${formatMoney(stats?.walletBalance ?? 0)}`],
                    ['Loyalty', String(stats?.loyaltyPoints ?? 0)],
                    ['Total spend', `${formatMoney(stats?.spend ?? 0)}`],
                  ].map(([k, v]) => (
                    <Stack key={k} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" color="text.secondary">{k}</Typography>
                      <Typography variant="body2" fontWeight={700}>{v || '—'}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={9} lg={9} sx={{ minWidth: 0 }}>
          <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 1, pt: 1, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
              <Tabs
                value={tab}
                onChange={(_, v) => {
                  setTab(v);
                  setSearch('');
                  setStatus('');
                  setFiltersOpen(false);
                }}
                variant="scrollable"
                allowScrollButtonsMobile
                TabIndicatorProps={{ sx: { display: 'none' } }}
              >
                {TABS.map((t) => (
                  <Tab
                    key={t.key}
                    value={t.key}
                    icon={t.icon}
                    iconPosition="start"
                    label={t.label}
                    sx={{
                      minHeight: 42,
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 1,
                      mx: 0.25,
                      color: 'text.secondary',
                      '&.Mui-selected': { bgcolor: TEAL, color: '#fff' },
                      '& .MuiTab-iconWrapper': { color: 'inherit' },
                    }}
                  />
                ))}
              </Tabs>
            </Box>

            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Typography fontWeight={800}>{tabLabel} List ({total})</Typography>
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
                <TextField
                  size="small"
                  placeholder={`Search ${tabLabel.toLowerCase()}…`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1, minWidth: 200, maxWidth: 420 }}
                />
                <Button
                  size="small"
                  variant={filtersOpen || extraFilterCount > 0 ? 'contained' : 'outlined'}
                  startIcon={<FilterList />}
                  onClick={() => setFiltersOpen((v) => !v)}
                >
                  Filters{extraFilterCount > 0 ? ` (${extraFilterCount})` : ''}
                </Button>
              </Stack>

              <Collapse in={filtersOpen} unmountOnExit>
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={800}>Advanced filters</Typography>
                    {extraFilterCount > 0 && (
                      <Button size="small" startIcon={<ClearAll />} onClick={clearFilters}>
                        Clear
                      </Button>
                    )}
                  </Stack>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth size="small" type="date" label="From date"
                        value={from} onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        fullWidth size="small" type="date" label="To date"
                        value={to} onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                      />
                    </Grid>
                    {statusOptions.length > 0 && (
                      <Grid item xs={12} sm={6} md={4}>
                        <TextField
                          select fullWidth size="small" label="Status"
                          value={status} onChange={(e) => setStatus(e.target.value)}
                          sx={{ '& .MuiInputBase-root': { bgcolor: 'background.paper' } }}
                        >
                          <MenuItem value="">All statuses</MenuItem>
                          {statusOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Collapse>

              {isLoading ? (
                <Skeleton height={320} />
              ) : (
                <EnhancedDataGrid
                  rows={rows}
                  columns={columns}
                  loading={isLoading}
                  getRowId={(r) => String(r._id)}
                  exportFileName={`${name}-${tab}`}
                  disableLocalSearch
                  autoHeight
                  onRowClick={(p) => openRow(p.row as Record<string, unknown>)}
                  slots={{
                    noRowsOverlay: () => (
                      <Typography color="text.secondary" align="center" sx={{ py: 8 }}>
                        No data available
                      </Typography>
                    ),
                  }}
                />
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
      )}

      <Dialog open={blockOpen} onClose={() => setBlockOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Block customer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Reason"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            placeholder="Blocked by admin"
            sx={{ mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBlockOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            disabled={blockMut.isPending}
            onClick={() => blockMut.mutate(blockReason || 'Blocked by admin')}
          >
            Block
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
